import * as THREE from "three";

export function createGrassMaterial(
    grassTexture: THREE.Texture,
    flowerTexture: THREE.Texture,
    noiseTexture: THREE.Texture,
    planeSize: [number, number],
    position: [number, number, number],
) {
    const material = new THREE.MeshStandardMaterial({
        map: grassTexture,
        transparent: true,
        alphaTest: 0.1,
        side: THREE.DoubleSide,
    });

    const customUniforms = {
        flowerTexture: { value: flowerTexture },
        noiseTexture: { value: noiseTexture },
        planeSize: { value: new THREE.Vector2(planeSize[0], planeSize[1]) },
        planePosition: {
            value: new THREE.Vector3(position[0], position[1], position[2]),
        },
        time: { value: 0 },
        windStrength: { value: 0.2 },
        windSpeed: { value: 0.2 },
        windDirection: { value: new THREE.Vector2(1.0, 0.5) },
    };

    material.userData.uniforms = customUniforms;

    material.onBeforeCompile = (shader) => {
        shader.uniforms = { ...shader.uniforms, ...customUniforms };

        ////////// VERTEX //////////
        shader.vertexShader = shader.vertexShader.replace(
            "#include <common>",
            `
            #include <common>
            uniform sampler2D noiseTexture;
            uniform vec2 planeSize;
            uniform vec3 planePosition;
            uniform float time;
            uniform float windStrength;
            uniform float windSpeed;
            uniform vec2 windDirection;
            varying vec3 vInstancePos;
            varying float vInstanceVariation;
            varying vec2 vNoiseUv;
            `,
        );

        shader.vertexShader = shader.vertexShader.replace(
            "#include <begin_vertex>",
            `
            #include <begin_vertex>
            vec4 worldPos = modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
            vec3 localPos = worldPos.xyz - planePosition;
            vInstancePos = localPos;
            vNoiseUv = (localPos.xz + planeSize * 0.5) / planeSize;


            vec2 noiseUV = fract((worldPos.xz / planeSize * 0.5) * 5.0 + time * windSpeed * 0.1);

            float noise1 = texture2D(noiseTexture, noiseUV * 0.81 + vec2(-0.2)).g;
            float noise2 = texture2D(noiseTexture, noiseUV * 2.3 + vec2(0.5)).r;
            float windNoise = noise1 * 0.712098409 + noise2 * 0.3123901;

            float heightFactor = uv.y;
            heightFactor = pow(heightFactor, 2.0);

            float angle = texture2D(noiseTexture, noiseUV + 0.32123).g * 6.28318530718;
            angle += texture2D(noiseTexture, noiseUV * 2.7 + 0.71).r * 1.5;
            vec2 newWindDir = vec2(cos(angle), sin(angle));

            vec2 windOffset = newWindDir * windNoise * windStrength * heightFactor;

            transformed.xz += windOffset;

            float instanceRandom = fract(sin(dot(worldPos.xz, vec2(12.98987, 78.233))));
            vInstanceVariation = instanceRandom;
            
            if (vInstanceVariation > 0.995) {
                transformed.y += 0.05 * heightFactor;
            }

            if (vInstanceVariation > 0.5) {
                vMapUv.x = 1.0 - vMapUv.x;
            }
            
            if(0.49 <= vInstanceVariation && vInstanceVariation <= 0.50) transformed.y += 0.1;
            transformed.y += 0.2;
            `,
        );

        // so lighting works and it billboards
        shader.vertexShader = shader.vertexShader.replace(
            "#include <project_vertex>",
            `
            #include <project_vertex>

            vec4 mv_Position = modelViewMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
            mv_Position.xyz += transformed;
            gl_Position = projectionMatrix * mv_Position;

            vNormal = normalize(normalMatrix * vec3(0.0, 1.0, 0.0)); // lighting
            `,
        );

        ////////// FRAGMENT //////////
        shader.fragmentShader = shader.fragmentShader.replace(
            "#include <common>",
            `
            #include <common>
            uniform sampler2D flowerTexture;
            uniform sampler2D noiseTexture;
            // uniform vec2 planeSize;
            varying vec3 vInstancePos;
            varying float vInstanceVariation;
            varying vec2 vNoiseUv;
            `,
        );

        shader.fragmentShader = shader.fragmentShader.replace(
            "#include <color_fragment>",
            `
            #include <color_fragment>
            
            bool grassColor = true;
            if(0.49 < vInstanceVariation && vInstanceVariation < 0.50) {
                diffuseColor = texture2D(flowerTexture, vMapUv);
                if (diffuseColor.a <= 0.01) discard;
                if (dot(diffuseColor.rgb, vec3(1.0)) <= 0.01) {
                    grassColor = false;
                    diffuseColor.rgba = vec4(2.0, 2.0, 0.0, 1.0);
                }
            } 
            
            if (grassColor) {
                vec3 c1 = vec3(0.25, 0.6662, 0.118);
                vec3 c2 = vec3(0.357, 0.761, 0.153);
                vec3 c3 = vec3(0.507, 0.83, 0.170);
    
                vec4 noise = texture2D(noiseTexture, vNoiseUv);
                
                vec3 finalColor = c3;
                finalColor = mix(finalColor, c2, smoothstep(0.47, 0.47, noise.r));
                finalColor = mix(finalColor, c1, smoothstep(0.53, 0.53, noise.b));
                
                
                if (vInstanceVariation > 0.995) {
                    finalColor *= vec3(0.95, 0.95, 1.0);
                }
                
                diffuseColor.rgb *= finalColor;
            }
            `,
        );
    };

    return material;
}
